import type { ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'

import type { AreaId } from '@/lib/module-routes'
import { usePermissions } from '@/lib/use-permissions'
import { useSelectedEntityId } from '@/lib/use-selected-entity'
import {
  activeNavigationItem,
  areaById,
  areaSections,
  navigationItems,
} from '@/app/module-registry'

import { IsolatedContribution } from './isolated-contribution'
import {
  ProductSidebar,
  ProductSidebarItem,
  ProductSidebarSection,
} from './product-sidebar'

export function AreaSidebar({
  area,
  children,
}: {
  area: AreaId
  children?: ReactNode
}) {
  const entityId = useSelectedEntityId()
  const leafFullPath = useRouterState({ select: (s) => s.matches.at(-1)?.fullPath })
  const { can } = usePermissions()

  const visible = navigationItems(area).filter(
    (item) =>
      (!item.permission || can(item.permission)) &&
      (entityId !== null || !String(item.to).includes('$entityId')),
  )
  if (entityId === null && visible.length === 0) return null

  const activeItem = activeNavigationItem(area, leafFullPath)
  const linkParams = (entityId === null ? {} : { entityId }) as never
  const { label } = areaById(area)
  const sections = areaSections(area)

  return (
    <ProductSidebar label={label}>
      {sections.map((section, sectionIndex) => {
        const sectionItems = visible.filter((item) => item.section === section.id)
        if (sectionItems.length === 0) return null

        return (
          <ProductSidebarSection
            key={section.id}
            label={section.label}
            divided={sectionIndex > 0}
          >
            {sectionItems.map((item) => (
              <IsolatedContribution key={String(item.to)}>
                <ProductSidebarItem
                  label={item.label}
                  icon={item.icon}
                  active={item === activeItem}
                >
                  {(props) => (
                    <Link to={item.to} params={linkParams} preload="render" {...props} />
                  )}
                </ProductSidebarItem>
              </IsolatedContribution>
            ))}
          </ProductSidebarSection>
        )
      })}
      <IsolatedContribution>{children}</IsolatedContribution>
    </ProductSidebar>
  )
}
